function GateBadge({ label, variant = "mint" }) {
  const styles = {
    mint: "text-mint bg-[rgba(109,211,167,.08)] border-[rgba(109,211,167,.15)]",
    gold: "text-gold bg-[rgba(242,185,73,.08)] border-[rgba(242,185,73,.18)]",
    dim: "text-t-sec bg-[rgba(148,180,200,.06)] border-[rgba(148,180,200,.12)]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[.58rem] tracking-[.14em] uppercase font-bold border rounded-full px-[9px] py-[3px] mb-[.9rem] w-fit font-ibm ${styles[variant]}`}
    >
      <span
        className={`w-[5px] h-[5px] rounded-full bg-current ${variant === "dim" ? "opacity-50" : "blink-dot"}`}
      />
      {label}
    </span>
  );
}

function Tag({ children }) {
  return (
    <span className="text-[.64rem] font-medium bg-[rgba(247,242,233,.05)] border border-[rgba(247,242,233,.1)] text-t-sec px-[9px] py-[3px] rounded-full whitespace-nowrap">
      {children}
    </span>
  );
}

function Route({ from, to }) {
  return (
    <div className="flex items-center gap-2 text-[.7rem] text-t-dim mb-[1.1rem] font-ibm">
      <span className="text-t-sec font-medium">{from}</span>
      <span
        className="flex-1 h-px min-w-[20px]"
        style={{
          background:
            "repeating-linear-gradient(90deg,#3D6278 0,#3D6278 4px,transparent 4px,transparent 8px)",
        }}
      />
      <span className="text-[.65rem] text-mint">✦</span>
      <span
        className="flex-1 h-px min-w-[20px]"
        style={{
          background:
            "repeating-linear-gradient(90deg,#3D6278 0,#3D6278 4px,transparent 4px,transparent 8px)",
        }}
      />
      <span className="text-t-sec font-medium">{to}</span>
    </div>
  );
}

function Meta({ items }) {
  return (
    <div className="grid grid-cols-2 gap-2 mb-[1.2rem]">
      {items.map(({ label, value, gold }) => (
        <div key={label}>
          <span className="block text-[.58rem] tracking-[.1em] uppercase text-t-dim mb-[2px]">
            {label}
          </span>
          <span
            className={`font-cairo text-[.9rem] font-bold ${gold ? "text-gold" : "text-t-primary"}`}
          >
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

function CardCta({ children, gold }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-cairo text-[.82rem] font-bold mt-auto pt-[.3rem] transition-all duration-200 group-hover:gap-[10px] ${gold ? "text-gold" : "text-mint"}`}
    >
      <svg
        className="w-[13px] h-[13px] flex-shrink-0 transition-transform duration-200 group-hover:-translate-x-[3px]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        aria-hidden="true"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
      {children}
    </span>
  );
}

/* ── Card accent line on hover ── */
const accentLine = (color) => (
  <span
    className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 origin-right transition-transform duration-[350ms] ease-[cubic-bezier(.4,0,.2,1)] group-hover:scale-x-100"
    style={{ background: color }}
  />
);

export default function Programs() {
  return (
    <section
      className="relative overflow-hidden px-[clamp(16px,5vw,80px)] py-[clamp(64px,9vh,110px)] pb-[clamp(48px,7vh,90px)]"
      id="programs"
    >
      {/* top divider */}
      <div
        className="absolute top-0 left-[clamp(16px,5vw,80px)] right-[clamp(16px,5vw,80px)] h-px"
        style={{
          background:
            "linear-gradient(90deg,transparent,rgba(109,211,167,0.11) 30%,rgba(109,211,167,0.11) 70%,transparent)",
        }}
      />

      {/* bg glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 0%,rgba(109,211,167,0.04) 0%,transparent 70%)",
        }}
      />

      {/* ── Header ── */}
      <div
        className="flex flex-col items-center text-center mb-[clamp(36px,5vh,60px)] relative z-[2] anim-up"
        style={{ animationDelay: ".05s" }}
      >
        <span className="inline-flex items-center gap-2 text-[.7rem] tracking-[.18em] uppercase text-mint font-semibold mb-[1.2rem] before:content-['◆'] before:text-[.45rem] before:opacity-50 after:content-['◆'] after:text-[.45rem] after:opacity-50">
          البوابات المتاحة
        </span>
        <h2
          className="font-cairo font-black leading-[1.1] tracking-tight text-t-primary mb-[.7rem]"
          style={{ fontSize: "clamp(1.9rem,3.5vw,3.1rem)" }}
        >
          اختر <em className="not-italic text-mint">وجهتك</em>
        </h2>
        <p
          className="text-t-sec leading-[1.85] max-w-[520px]"
          style={{ fontSize: "clamp(.88rem,.95rem,1rem)" }}
        >
          كل شخص يبدأ من نقطة مختلفة. عندنا رحلة تناسب كل مرحلة — سواء كنت تبغى
          تغوص عميق، تكتسب مهارة بسرعة، أو لسه تكتشف نفسك.
        </p>
      </div>

      {/* ── Cards Grid ── */}
      <div
        className="relative z-[2] mb-[clamp(28px,4vh,48px)] grid gap-[clamp(10px,1.2vw,16px)]"
        style={{ gridTemplateColumns: "repeat(3,1fr)" }}
      >
        {/* ① FEATURED: المسارات المكثفة */}
        <a
          href="https://store.afaaqwetaan.com"
          target="_blank"
          rel="noopener"
          className="group col-span-2 relative flex flex-row gap-[clamp(20px,3vw,36px)] items-start bg-gradient-to-br from-[#0C2240] to-[#0A1C30] border border-[rgba(109,211,167,0.11)] rounded-[14px] p-[clamp(20px,2.2vw,28px)] overflow-hidden no-underline text-inherit transition-all duration-300 hover:-translate-y-[3px] hover:border-[rgba(109,211,167,0.28)] hover:shadow-[0_16px_40px_rgba(8,24,40,.5)] anim-up"
          style={{ animationDelay: ".12s" }}
        >
          {accentLine("#6DD3A7")}
          {/* glow */}
          <span className="absolute -top-10 -right-10 w-[120px] h-[120px] rounded-full bg-mint opacity-0 blur-[40px] transition-opacity duration-400 group-hover:opacity-[.06] pointer-events-none" />

          {/* Main content */}
          <div className="flex-1 flex flex-col">
            <GateBadge label="G-01 · BOARDING NOW" />
            <span className="text-[2rem] mb-[.7rem] leading-none block">
              ✈️
            </span>
            <h3
              className="font-cairo font-extrabold text-t-primary mb-[.4rem] tracking-tight"
              style={{
                fontSize: "clamp(1.25rem,1.8vw,1.6rem)",
                lineHeight: 1.15,
              }}
            >
              المسارات المكثفة
            </h3>
            <p
              className="text-t-sec leading-[1.75] mb-[1rem] max-w-[380px]"
              style={{ fontSize: "clamp(.8rem,.85vw,.88rem)" }}
            >
              رحلة 10 أسابيع تأخذك من المعرفة النظرية إلى مشروع حقيقي تُقدّمه
              أمام لجنة تقييم. تدريب يومي، خبراء من السوق، ومجتمع يرافقك في كل
              محطة.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-[1.1rem]">
              <Tag>الذكاء الاصطناعي في الأعمال</Tag>
              <Tag>الأمن السيبراني</Tag>
            </div>
            <Route from="الآن" to="سوق العمل" />
            <Meta
              items={[
                { label: "وقت الرحلة", value: "10 Weeks" },
                { label: "السعر", value: "2000 – 2500 SAR", gold: true },
              ]}
            />
            <CardCta>احجز مقعدك</CardCta>
          </div>

          {/* Stats sidebar */}
          <div
            className="flex flex-col gap-2.5 flex-shrink-0 w-[clamp(130px,20%,180px)] p-[clamp(14px,1.5vw,20px)] bg-[rgba(8,24,40,.5)] border border-[rgba(109,211,167,.1)] rounded-[8px] self-stretch justify-center"
            aria-label="إحصائيات المسار"
          >
            {[
              { n: "+400", l: "ساعة\nتدريبية" },
              { n: "+30", l: "مهمة\nحقيقية" },
              { n: "+10", l: "خبير\nالصناعة" },
              { n: "9.25", l: "تقييم\nالمتدربين" },
            ].map(({ n, l }, i) => (
              <div key={n}>
                {i > 0 && (
                  <div className="h-px bg-[rgba(109,211,167,.08)] mb-2.5" />
                )}
                <div className="text-center">
                  <span className="font-cairo text-[1.5rem] font-black text-mint block leading-none mb-[3px]">
                    {n}
                  </span>
                  <span className="text-[.6rem] text-t-dim leading-[1.3] whitespace-pre-line">
                    {l}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </a>

        {/* ② المعسكرات التدريبية */}
        <a
          href="https://store.afaaqwetaan.com"
          target="_blank"
          rel="noopener"
          className="group relative flex flex-col bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] p-[clamp(20px,2.2vw,28px)] overflow-hidden no-underline text-inherit transition-all duration-300 hover:-translate-y-[3px] hover:border-[rgba(109,211,167,0.28)] hover:shadow-[0_16px_40px_rgba(8,24,40,.5)] anim-up"
          style={{ animationDelay: ".2s" }}
        >
          {accentLine("#6DD3A7")}
          <span className="absolute -top-10 -right-10 w-[120px] h-[120px] rounded-full bg-mint opacity-0 blur-[40px] transition-opacity duration-400 group-hover:opacity-[.06] pointer-events-none" />
          <GateBadge label="G-02 · OPEN" />
          <span className="text-[1.6rem] mb-[.7rem] leading-none block">
            ⛺
          </span>
          <h3
            className="font-cairo font-extrabold text-t-primary mb-[.4rem] tracking-tight"
            style={{ fontSize: "clamp(1rem,1.4vw,1.25rem)", lineHeight: 1.15 }}
          >
            المعسكرات التدريبية
          </h3>
          <p
            className="text-t-sec leading-[1.75] mb-[1rem]"
            style={{ fontSize: "clamp(.8rem,.85vw,.88rem)" }}
          >
            مهارة محددة، 3 إلى 4 أيام مكثفة، تطبيق فوري. مثالي إذا أردت اكتساب
            أداة جديدة أو استكشاف مجال قبل الالتزام بمسار.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-[1.1rem]">
            {["Power BI", "Python", "AI Tools", "SQL", "Azure", "LinkedIn"].map(
              (t) => (
                <Tag key={t}>{t}</Tag>
              ),
            )}
          </div>
          <Route from="الآن" to="مهارة جديدة" />
          <Meta
            items={[
              { label: "وقت الرحلة", value: "1-2 Weeks" },
              { label: "الشهادة", value: "Certified ✓" },
            ]}
          />
          <CardCta>استعرض المعسكرات</CardCta>
        </a>

        {/* ③ الدورات المسجّلة */}
        <a
          href="https://store.afaaqwetaan.com"
          target="_blank"
          rel="noopener"
          className="group relative flex flex-col bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] p-[clamp(20px,2.2vw,28px)] overflow-hidden no-underline text-inherit transition-all duration-300 hover:-translate-y-[3px] hover:border-[rgba(109,211,167,0.28)] hover:shadow-[0_16px_40px_rgba(8,24,40,.5)] anim-up"
          style={{ animationDelay: ".27s" }}
        >
          {accentLine("#7BAFC4")}
          <span className="absolute -top-10 -right-10 w-[120px] h-[120px] rounded-full bg-[#7BAFC4] opacity-0 blur-[40px] transition-opacity duration-400 group-hover:opacity-[.06] pointer-events-none" />
          <GateBadge label="G-03 · 24/7" variant="dim" />
          <span className="text-[1.6rem] mb-[.7rem] leading-none block">
            🎬
          </span>
          <h3
            className="font-cairo font-extrabold text-t-primary mb-[.4rem] tracking-tight"
            style={{ fontSize: "clamp(1rem,1.4vw,1.25rem)", lineHeight: 1.15 }}
          >
            الدورات المسجّلة
          </h3>
          <p
            className="text-t-sec leading-[1.75] mb-[1rem]"
            style={{ fontSize: "clamp(.8rem,.85vw,.88rem)" }}
          >
            نفس المحتوى، بمرونة كاملة. تشوفها متى تبغى، تكرر اللي ما فهمته،
            وتتقدم بوتيرتك أنت.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-[1.1rem]">
            {["تحليل البيانات", "علم البيانات", "الذكاء الاصطناعي"].map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </div>
          <Route from="أي وقت" to="بوتيرتك" />
          <Meta
            items={[
              { label: "الوصول", value: "دائم" },
              { label: "النوع", value: "Self-paced" },
            ]}
          />
          <CardCta>استعرض الدورات</CardCta>
        </a>

        {/* ④ باقة مواطن القوة */}
        <a
          href="https://store.afaaqwetaan.com"
          target="_blank"
          rel="noopener"
          className="group relative flex flex-col bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] p-[clamp(20px,2.2vw,28px)] overflow-hidden no-underline text-inherit transition-all duration-300 hover:-translate-y-[3px] hover:border-[rgba(242,185,73,0.28)] hover:shadow-[0_16px_40px_rgba(8,24,40,.5)] anim-up"
          style={{ animationDelay: ".34s" }}
        >
          {accentLine("#F2B949")}
          <span className="absolute -top-10 -right-10 w-[120px] h-[120px] rounded-full bg-gold opacity-0 blur-[40px] transition-opacity duration-400 group-hover:opacity-[.06] pointer-events-none" />
          <GateBadge label="G-04 · OPEN" variant="gold" />
          <span className="text-[1.6rem] mb-[.7rem] leading-none block">
            🌟
          </span>
          <h3
            className="font-cairo font-extrabold text-t-primary mb-[.4rem] tracking-tight"
            style={{ fontSize: "clamp(1rem,1.4vw,1.25rem)", lineHeight: 1.15 }}
          >
            باقة مواطن القوة
          </h3>
          <p
            className="text-t-sec leading-[1.75] mb-[1rem]"
            style={{ fontSize: "clamp(.8rem,.85vw,.88rem)" }}
          >
            مو عارف وين تبدأ؟ ابدأ بنفسك. اكتشف نقاط قوتك الحقيقية مع اختبار
            Gallup وكوتش معتمد CIPD.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-[1.1rem]">
            {["Gallup CliftonStrengths", "كوتش معتمد", "جلسة فردية"].map(
              (t) => (
                <Tag key={t}>{t}</Tag>
              ),
            )}
          </div>
          <Route from="من أنت؟" to="وجهتك الصحيحة" />
          <Meta
            items={[
              { label: "وقت الرحلة", value: "3-4 أيام" },
              { label: "الأسلوب", value: "أونلاين" },
            ]}
          />
          <CardCta gold>اكتشف قوتك</CardCta>
        </a>

        {/* ⑤ الورش التفاعلية */}
        <a
          href="https://store.afaaqwetaan.com"
          target="_blank"
          rel="noopener"
          className="group relative flex flex-col bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] p-[clamp(20px,2.2vw,28px)] overflow-hidden no-underline text-inherit transition-all duration-300 hover:-translate-y-[3px] hover:border-[rgba(109,211,167,0.28)] hover:shadow-[0_16px_40px_rgba(8,24,40,.5)] anim-up"
          style={{ animationDelay: ".41s" }}
        >
          {accentLine("#9BBDD0")}
          <span className="absolute -top-10 -right-10 w-[120px] h-[120px] rounded-full bg-[#9BBDD0] opacity-0 blur-[40px] transition-opacity duration-400 group-hover:opacity-[.06] pointer-events-none" />
          <GateBadge label="G-05 · SOON" variant="dim" />
          <span className="text-[1.6rem] mb-[.7rem] leading-none block">
            🎯
          </span>
          <h3
            className="font-cairo font-extrabold text-t-primary mb-[.4rem] tracking-tight"
            style={{ fontSize: "clamp(1rem,1.4vw,1.25rem)", lineHeight: 1.15 }}
          >
            الورش التفاعلية
          </h3>
          <p
            className="text-t-sec leading-[1.75] mb-[1rem]"
            style={{ fontSize: "clamp(.8rem,.85vw,.88rem)" }}
          >
            ساعتان تفرقان. جلسة مركّزة على موضوع واحد بعمق، مع تطبيق فوري تخرج
            به.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-[1.1rem]">
            {["أدوات AI", "LinkedIn", "الأمن السيبراني", "مهارات مهنية"].map(
              (t) => (
                <Tag key={t}>{t}</Tag>
              ),
            )}
          </div>
          <Route from="الآن" to="90 دقيقة" />
          <Meta
            items={[
              { label: "المدة", value: "90–120 د" },
              { label: "السعر", value: "260 ر", gold: true },
            ]}
          />
          <CardCta>استعرض الورش</CardCta>
        </a>
      </div>
      {/* end grid */}

      {/* ── Bottom CTA Bar ── */}
      <div
        className="relative z-[2] flex items-center justify-center gap-[clamp(12px,2vw,24px)] flex-wrap px-[clamp(20px,3vw,36px)] py-[clamp(18px,2.5vh,26px)] bg-[#0C1F35] border border-[rgba(109,211,167,0.11)] rounded-[14px] anim-up"
        style={{ animationDelay: ".48s" }}
      >
        <p
          className="text-t-sec"
          style={{ fontSize: "clamp(.85rem,.92rem,1rem)" }}
        >
          <strong className="text-t-primary">
            مو متأكد من الوجهة المناسبة؟
          </strong>{" "}
          انضم للمجتمع مجاناً وتعرّف أكثر قبل ما تقرر.
        </p>
        <span className="w-px h-6 bg-[rgba(109,211,167,0.11)] hidden sm:block" />
        <a
          href="https://store.afaaqwetaan.com"
          target="_blank"
          rel="noopener"
          className="font-cairo font-bold bg-gold text-ink px-[26px] py-[11px] rounded-[4px] no-underline whitespace-nowrap inline-flex items-center gap-1.5 transition-all duration-200 hover:opacity-90 hover:-translate-y-px"
          style={{ fontSize: "clamp(.86rem,.92rem,.96rem)" }}
        >
          <svg
            className="w-[14px] h-[14px]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          استعرض كل البرامج
        </a>
        <a
          href="#community"
          className="font-cairo font-medium border border-[rgba(247,242,233,.16)] text-t-sec px-[22px] py-[11px] rounded-[4px] no-underline whitespace-nowrap transition-all duration-200 hover:border-[rgba(247,242,233,.35)] hover:text-t-primary"
          style={{ fontSize: "clamp(.84rem,.9rem,.92rem)" }}
        >
          انضم للمجتمع مجاناً
        </a>
      </div>
    </section>
  );
}
